/**
 * PropertyUrlInput Component
 *
 * Step 1 of the property video wizard: Enter a property listing URL.
 * Provides URL input with validation, loading state, and error handling.
 */

import { useState, useCallback } from 'react';
import { Loader2, Link } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { IPropertyUrlInputProps } from '@/@types/property';

/**
 * Validates if a string is a valid HTTP/HTTPS URL
 */
const isValidUrl = (url: string): boolean => {
  const trimmed = url.trim();
  return trimmed.startsWith('http://') || trimmed.startsWith('https://');
};

const PropertyUrlInput = ({
  onSubmit,
  isLoading = false,
  error = null,
  placeholder = 'https://www.bazaraki.com/property/...',
}: IPropertyUrlInputProps) => {
  const [url, setUrl] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleUrlChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setUrl(e.target.value);
      // Clear validation error when user starts typing
      if (validationError) {
        setValidationError(null);
      }
    },
    [validationError]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const trimmedUrl = url.trim();

      if (!trimmedUrl) {
        setValidationError('Please enter a URL');
        return;
      }

      if (!isValidUrl(trimmedUrl)) {
        setValidationError('URL must start with http:// or https://');
        return;
      }

      setValidationError(null);
      await onSubmit(trimmedUrl);
    },
    [url, onSubmit]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !isLoading) {
        // Form will handle submit via onSubmit
        return;
      }
    },
    [isLoading]
  );

  const displayError = validationError || error;

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-2xl space-y-4">
      <div className="space-y-2">
        <Label htmlFor="property-url" className="flex items-center gap-2">
          <Link className="h-4 w-4" />
          Property Listing URL
        </Label>
        <Input
          id="property-url"
          type="url"
          value={url}
          onChange={handleUrlChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isLoading}
          aria-invalid={!!displayError}
          aria-describedby={displayError ? 'url-error' : 'url-helper'}
          className="w-full"
        />
        {displayError ? (
          <p id="url-error" className="text-sm text-destructive">
            {displayError}
          </p>
        ) : (
          <p id="url-helper" className="text-sm text-muted-foreground">
            Paste a property listing URL from bazaraki.com or other supported sites
          </p>
        )}
      </div>

      <Button type="submit" disabled={isLoading} className="w-full sm:w-auto">
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Scraping...
          </>
        ) : (
          'Scrape Property'
        )}
      </Button>
    </form>
  );
};

export { PropertyUrlInput };
